import { useEffect, useRef } from "react";

import Ep59Img from "../../assets/images/conversations/ep-59-women-leaders.webp";
import Ep58Img from "../../assets/images/conversations/ep-58-Philip-GREEN.webp";
import Ep53Img from "../../assets/images/conversations/ep-53-Shardul-Kulkarni.webp";
import Ep42Img from "../../assets/images/conversations/ep-42-Siddharth-Mayur.webp";
import Ep33Img from "../../assets/images/conversations/ep-33-Green-Hydrogen.webp";

/* ─────────────────────────────────────────────
   PREFIX: hy-
───────────────────────────────────────────── */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&family=Roboto:wght@400;500&display=swap');

  *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

  .hy-page {
    background: #1A1A1A;
    min-height: 100vh;
    font-family: 'Roboto', sans-serif;
    color: #fff;
  }

  .hy-inner {
    max-width: 1600px;
    margin: 0 auto;
    padding-left: 102px;
    padding-right: 102px;
  }

  .hy-page-title {
    font-family: 'Montserrat', sans-serif;
    font-size: 50px;
    font-weight: 700;
    text-align: center;
    color: #ffffff;
    padding-top: 60px;
    padding-bottom: 60px;
    letter-spacing: -0.5px;
  }

  .hy-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    column-gap: 30px;
    row-gap: 60px;
    padding-bottom: 100px;
  }

  .hy-card-wrap {
    display: flex;
    flex-direction: column;
    opacity: 0;
    transform: translateY(40px);
    transition: opacity 0.7s ease, transform 0.7s ease;
  }
  .hy-card-wrap.hy-visible {
    opacity: 1;
    transform: translateY(0);
  }
  .hy-card-wrap:nth-child(2) { transition-delay: 0.08s; }
  .hy-card-wrap:nth-child(3) { transition-delay: 0.16s; }
  .hy-card-wrap:nth-child(4) { transition-delay: 0.08s; }
  .hy-card-wrap:nth-child(5) { transition-delay: 0.16s; }
  .hy-card-wrap:nth-child(6) { transition-delay: 0.24s; }

  .hy-card {
    border-radius: 10px;
    overflow: hidden;
    cursor: pointer;
    transition: transform 0.35s ease;
    box-shadow: none;
  }
  .hy-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 48px rgba(116, 63, 172, 0.35);
  }
  .hy-card img {
    width: 100%;
    height: 300px;
    display: block;
    border-radius: 10px;
  }

  .hy-card__title {
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: #ffffff;
    line-height: 140%;
    margin-top: 20px;
  }
  .hy-card__read-more {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: 'Roboto', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #743FAC;
    text-decoration: underline;
    transition: gap 0.25s ease, color 0.25s ease;
    margin-top: 16px;
  }
  .hy-card__read-more:hover {
    gap: 8px;
    color: #9B5FD4;
  }
  .hy-card__read-more svg {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
  }

  /* ── TABLET (max 768px) ── */
  @media (max-width: 768px) {
    .hy-inner { padding-left: 32px; padding-right: 32px; }
    .hy-page-title { font-size: 32px; padding-top: 32px; padding-bottom: 32px; }
    .hy-grid {
      grid-template-columns: repeat(2, 1fr);
      column-gap: 20px;
      row-gap: 40px;
      padding-bottom: 48px;
    }
    .hy-card img { height: 220px; }
    .hy-card__title { margin-top: 18px; font-size: 15px; }
    .hy-card__read-more { margin-top: auto; }
  }

  /* ── MOBILE (max 480px) ── */
  @media (max-width: 480px) {
    .hy-inner { padding-left: 16px; padding-right: 16px; }
    .hy-page-title { font-size: 30px; padding-top: 51px; padding-bottom: 30px; }
    .hy-grid {
      grid-template-columns: 1fr;
      column-gap: 0;
      row-gap: 40px;
    }
    .hy-card img { height: auto; }
    .hy-card__title { font-size: 14px; margin-top: 15px; }
    .hy-card__read-more { margin-top: 10px; }
  }
`;

function ArrowIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

/* ─────────────────────────────────────────────
   HYDROGEN EPISODE DATA — sourced from energizingindia.tv/hydrogen/
───────────────────────────────────────────── */
const EPISODES = [
  {
    img: Ep59Img,
    alt: "EP 59 Women Leaders in Hydrogen",
    title: "EP 59 | Women Leaders in Hydrogen – India",
    href: "/ep59womenleaderinhydrogen/",
  },
  {
    img: Ep58Img,
    alt: "EP 58 Philip Green",
    title: "EP 58 | Australia & India: Adapting to the New Global Reality | Philip GREEN | Australia's High Commissioner to India",
    href: "/ep58philipgreen/",
  },
  {
    img: Ep53Img,
    alt: "EP 53 Shardul Kulkarni",
    title: "EP 53 | Industrial Decarbonisation with Green Hydrogen | Shardul Kulkarni",
    href: "/ep-53-industrial-decarbonisation-with-green-hydrogen-shardul-kulkarni/",
  },
  {
    img: Ep42Img,
    alt: "EP 42 Siddharth Mayur",
    title: "EP 42 | The ABCs of Green Hydrogen | H2e Power | Siddharth Mayur",
    href: "/ep-42-the-abcs-of-green-hydrogen-h2e-power-siddharth-mayur/",
  },
  {
    img: Ep33Img,
    alt: "EP 33 Green Hydrogen",
    title: "EP | 33 Decarbonizing the Future: Exploring Small-Scale Off-Grid Generation of Green Hydrogen",
    href: "/ep-33-decarbonizing-the-future-exploring-small-scale-off-grid-generation-of-green-hydrogen/",
  },
];

/* ─────────────────────────────────────────────
   CARD
───────────────────────────────────────────── */
function EpisodeCard({ img, alt, title, href }) {
  return (
    <div className="hy-card-wrap">
      <a href={href} className="hy-card" rel="noreferrer">
        <img src={img} alt={alt} />
      </a>
      <p className="hy-card__title">{title}</p>
      <a href={href} className="hy-card__read-more" rel="noreferrer">
        Read More <ArrowIcon />
      </a>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ROOT
───────────────────────────────────────────── */
export default function HydrogenEpisodes() {
  const gridRef = useRef(null);

  useEffect(() => {
    const cards = gridRef.current?.querySelectorAll(".hy-card-wrap") || [];
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("hy-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    cards.forEach((card) => observer.observe(card));
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <style>{CSS}</style>
      <div className="hy-page">
        <div className="hy-inner">
          <h1 className="hy-page-title">Hydrogen Episodes</h1>
          <div className="hy-grid" ref={gridRef}>
            {EPISODES.map((ep, i) => (
              <EpisodeCard key={i} {...ep} />
            ))}
          </div>
        </div>
      </div>
    </>
  );
}